import React, { useState } from 'react';
import { MdVisibility, MdVisibilityOff, MdSunny, MdNightlight } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

import toast from 'react-hot-toast';
import RegisterModal from '../components/RegisterModal';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import LanguageSwitcher from '../components/LanguageSwitcher';
import loginVideo from '../assets/loginlogo.mp4';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!username.trim() || !password.trim()) {
      setError(t('login.usernameRequired'));
      return;
    }

    setError('');
    setLoading(true);

    try {
      const user = await login(username.trim(), password);
      toast.success(`${t('login.title')}! ${user.username || ''}`);
      navigate(user.role === 'admin' ? '/admin' : '/company', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.message || t('login.loginFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="nl-container">
      {/* Left column: sign-in form */}
      <section className="nl-left">
        <div className="nl-form-wrapper">
          {/* Top Controls */}
          <div className="animate-element" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 28, position: 'relative', zIndex: 10 }}>
            <LanguageSwitcher />
            <button
              className="nl-theme-toggle"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Light Theme' : 'Dark Theme'}
              style={{ position: 'static' }}
            >
              {theme === 'dark' ? <MdSunny size={20} /> : <MdNightlight size={20} />}
            </button>
          </div>

          <h1 className="nl-title animate-element animate-delay-100">Nortech Cloud {t('common.system') || 'System'} </h1>
          <p className="nl-subtitle animate-element animate-delay-200">
            {t('login.subtitle')}
          </p>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="animate-element" style={{ color: 'var(--danger)', fontSize: '0.875rem', marginBottom: '16px', padding: '12px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '12px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                ⚠️ {error}
              </div>
            )}

            <div className="nl-form-group animate-element animate-delay-300">
              <label className="nl-label" htmlFor="login-username">{t('login.username')}</label>
              <div className="nl-glass-input">
                <input 
                  id="login-username"
                  name="username" 
                  type="text" 
                  placeholder={t('login.usernamePlaceholder')} 
                  className="nl-input"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoComplete="username"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="nl-form-group animate-element animate-delay-400">
              <label className="nl-label" htmlFor="login-password">{t('login.password')}</label>
              <div className="nl-glass-input">
                <input 
                  id="login-password"
                  name="password" 
                  type={showPassword ? 'text' : 'password'} 
                  placeholder={t('login.passwordPlaceholder')} 
                  className="nl-input" 
                  style={{ paddingRight: '48px' }}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)} 
                  tabIndex={-1}
                  style={{ position: 'absolute', right: '16px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex' }}
                >
                  {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                </button>
              </div>
            </div>

            <div className="animate-element animate-delay-500" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <label className="nl-checkbox-wrapper">
                <input type="checkbox" className="nl-checkbox" />
                <span>{t('login.title') === 'Sign In' ? 'Remember me' : 'Oturumu açık tut'}</span>
              </label>
              <a href="#" onClick={(e) => { e.preventDefault(); setShowForgotPassword(true); }} style={{ fontSize: '0.875rem', color: 'var(--primary)', textDecoration: 'none' }}>
                {t('login.title') === 'Sign In' ? 'Forgot password' : 'Şifremi unuttum'}
              </a>
            </div>

            <button type="submit" className="nl-btn animate-element animate-delay-600" disabled={loading}>
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="spinner spinner-sm" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }}></span>
                  <span>{t('login.loggingIn')}</span>
                </div>
              ) : t('login.loginButton')}
            </button>
          </form>

          <div className="nl-divider animate-element animate-delay-700">
            <div className="nl-divider-line"></div>
            <span className="nl-divider-text">{t('login.title') === 'Sign In' ? 'or' : 'veya'}</span>
          </div>

          <button onClick={() => setShowRegister(true)} type="button" className="nl-create-account-btn animate-element animate-delay-800">
            {t('login.title') === 'Sign In' ? 'Create New Account' : 'Yeni Hesap Oluştur'}
          </button>
          
          <p className="animate-element animate-delay-900" style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '32px' }}>
            Nortech Veri Takip Sistemi © 2026
          </p>
        </div>
      </section>

      {/* Right column: hero image + testimonials */}
      <section className="nl-right">
        <div className="nl-hero animate-slide-right animate-delay-300">
          <video
            autoPlay
            loop
            muted
            playsInline
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0
            }}
          >
            <source src={loginVideo} type="video/mp4" />
          </video>

          {/* Overlay Content */}
          <div style={{
            position: 'absolute',
            top: '55%',
            left: '40px',
            right: '40px',
            zIndex: 10,
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '16px'
          }}>
             <h2 style={{ 
              fontFamily: "'Plus Jakarta Sans', sans-serif", 
              fontSize: '2.5rem', 
              fontWeight: 800, 
              lineHeight: 1.2,
              margin: 0,
              textShadow: '0 2px 10px rgba(0,0,0,0.5)'
            }}>
              {t('login.heroTitle')}
            </h2>
            <p style={{ 
              fontSize: '1.1rem', 
              lineHeight: 1.6, 
              fontWeight: 400,
              margin: 0,
              opacity: 0.95,
              textShadow: '0 2px 8px rgba(0,0,0,0.5)'
            }}>
              {t('login.heroText')}
            </p>
            <a 
              href="https://www.nortechse.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="nl-btn"
              style={{
                marginTop: '12px',
                width: 'fit-content',
                padding: '14px 28px',
                textDecoration: 'none',
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                borderRadius: '12px'
              }}
            >
              {t('login.heroBtn')}
            </a>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegister && <RegisterModal onClose={() => setShowRegister(false)} />}

      {/* Forgot Password Modal */}
      {showForgotPassword && <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />}
    </div>
  );
}
